import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import { functions } from '../../lib/firebase';

/**
 * setRoleClaim callable hook (admin 第 12 弾、Phase 5.4)。
 *
 * 旧 admin の setUserRole 呼出パターンを v2 callable に合わせて踏襲。master が他 user の
 * Custom Claims (`role`, `districts`) を設定する。
 *
 * 認可: callable 側 (functions/src/admin/setRoleClaim.ts) で `requireMaster` 強制。
 */

export type AssignableRole = 'master' | 'district' | 'none';

export interface SetRoleClaimInput {
  email: string;
  role: AssignableRole;
  districts?: string[];
}

export interface SetRoleClaimResult {
  uid: string;
  email: string;
  role: AssignableRole;
  districts: string[];
  updatedAt: string;
}

export type UseSetRoleClaimState =
  | { status: 'idle'; result: null; error: null }
  | { status: 'pending'; result: null; error: null }
  | { status: 'success'; result: SetRoleClaimResult; error: null }
  | { status: 'error'; result: null; error: Error };

export interface UseSetRoleClaimReturn {
  state: UseSetRoleClaimState;
  /** 成功時 result を resolve、失敗時は error を state に記録のうえ throw する (呼出側で catch して toast)。 */
  call: (input: SetRoleClaimInput) => Promise<SetRoleClaimResult>;
  reset: () => void;
}

export function useSetRoleClaim(): UseSetRoleClaimReturn {
  const [state, setState] = useState<UseSetRoleClaimState>({
    status: 'idle',
    result: null,
    error: null,
  });

  async function call(input: SetRoleClaimInput): Promise<SetRoleClaimResult> {
    setState({ status: 'pending', result: null, error: null });
    try {
      const fn = httpsCallable<SetRoleClaimInput, SetRoleClaimResult>(functions, 'setRoleClaim');
      const res = await fn(input);
      setState({ status: 'success', result: res.data, error: null });
      return res.data;
    } catch (e) {
      const err = e as Error & { code?: string };
      setState({ status: 'error', result: null, error: err });
      // 呼出側 (UsersAdminPage.handleChangeRole) で catch し、self-downgrade 等のサーバ側
      // failed-precondition メッセージをそのまま toast 表示する。
      throw err;
    }
  }

  function reset() {
    setState({ status: 'idle', result: null, error: null });
  }

  return { state, call, reset };
}
